import { useEffect, useRef, useState } from 'react';
import { Loader2, ScanSearch } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { importService, FileRef, ImportSource } from '@/services/api/import';

export default function Step3Detecting({
  source,
  fileRefs,
  localFiles,
  onReady,
  onError,
}: {
  source: ImportSource;
  fileRefs: FileRef[];
  localFiles: File[];
  onReady: (sessionId: string) => void;
  onError: () => void;
}) {
  const [phase, setPhase] = useState<'creating' | 'detecting'>('creating');
  const [progress, setProgress] = useState({ done: 0, total: fileRefs.length });
  const cancelled = useRef(false);

  useEffect(() => {
    void run();
    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    try {
      // 1. Create the session with the picked file refs.
      const { sessionId } = await importService.createSession({ source, fileRefs });

      // 2. Kick off detection. Mode is decided server-side.
      setPhase('detecting');
      const { mode } = await importService.startDetection(sessionId);

      // 3. Poll progress until state advances to 'review' (or 'done'/'failed').
      const poll = async (): Promise<void> => {
        if (cancelled.current) return;
        const prog = await importService.getProgress(sessionId);
        const totalDetected = (prog.byStatus.detected || 0) + (prog.byStatus.failed || 0)
                            + (prog.byStatus.uploaded || 0) + (prog.byStatus.skipped_dup || 0);
        setProgress({ done: totalDetected, total: Object.values(prog.byStatus).reduce((s, n) => s + n, 0) || fileRefs.length });
        if (prog.state === 'review' || prog.state === 'done') {
          onReady(sessionId);
          return;
        }
        if (prog.state === 'failed') {
          alert('Detection failed. Try again.');
          onError();
          return;
        }
        await new Promise(r => setTimeout(r, mode === 'sync' ? 200 : 1500));
        return poll();
      };
      await poll();
    } catch (e: any) {
      alert(`Detect failed: ${e.message || e}`);
      onError();
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <Card>
      <CardContent className="pt-8 pb-8 space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {phase === 'creating' ? <Loader2 className="h-7 w-7 animate-spin" /> : <ScanSearch className="h-7 w-7" />}
        </div>
        <div>
          <p className="text-base font-semibold">
            {phase === 'creating' ? 'Preparing import…' : 'Detecting document types…'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {phase === 'creating' ? 'Setting up your session' :
              `Classifying ${progress.done} / ${progress.total} files`}
          </p>
        </div>
        <Progress value={pct} className="max-w-md mx-auto" />
        <p className="text-[11px] text-muted-foreground">
          {phase === 'detecting' && 'Classifier uses filename + first-page text; only the ambiguous files cost cents.'}
        </p>
      </CardContent>
    </Card>
  );
}
